import * as THREE from "three";
import type { Scenery } from "../../prims/scenery";
import { mergeStatic } from "../merge";
import { onTick, type Kit } from "./kit";

// A Ferris wheel (#156): an A-frame either side, the wheel turning slowly on
// the render clock, and gondolas that hang level as it goes round. The wheel
// turns in the local x-y plane (across the piece's u axis), `h` tall overall.

const GONDOLA_COLORS = ["#ef476f", "#ffd166", "#06d6a0", "#118ab2", "#f78c6b", "#9b5de5"];
/** Radians a second: about a minute a turn. */
const SPIN = 0.1;

export function ferrisWheel(kit: Kit, s: Scenery): THREE.Object3D {
  const total = s.h ?? 18;
  const r = (total - 2.4) / 2;
  const hubY = r + 1.6;
  const rim = s.color ?? "#f4f1ea";
  const root = new THREE.Group();

  // Base: a ticket deck and the A-frames up to the hub.
  const base = new THREE.Group();
  kit.box(base, 6, 0.4, 5, "#c9b79c", 0, 0, 0, "concrete");
  kit.box(base, 1.4, 2.2, 1.4, "#ef476f", 2.2, 0.4, 1.6);
  kit.box(base, 1.7, 0.2, 1.7, "#ffd166", 2.2, 2.6, 1.6);
  for (const z of [-1.6, 1.6]) {
    const hub = new THREE.Vector3(0, hubY, z * 0.75);
    for (const x of [-r * 0.55, r * 0.55]) kit.rod(base, new THREE.Vector3(x, 0.4, z), hub, 0.22, "#8d99ae");
  }
  kit.rod(base, new THREE.Vector3(0, hubY, -1.6), new THREE.Vector3(0, hubY, 1.6), 0.35, "#5c677d");
  root.add(mergeStatic(base));

  // The wheel: two rims, spokes and cross-braces, turning about z.
  const wheel = new THREE.Group();
  wheel.position.y = hubY;
  const spokes = 16;
  for (const z of [-1.1, 1.1]) {
    const ring = new THREE.Mesh(new THREE.TorusGeometry(r, 0.13, 6, 48), kit.mat(rim, "metal", { roughness: 0.5 }));
    ring.position.z = z;
    ring.castShadow = true;
    wheel.add(ring);
    const inner = new THREE.Mesh(new THREE.TorusGeometry(r * 0.55, 0.08, 6, 32), kit.mat(rim, "metal", { roughness: 0.5 }));
    inner.position.z = z;
    wheel.add(inner);
    for (let i = 0; i < spokes; i++) {
      const a = (i / spokes) * Math.PI * 2;
      kit.rod(wheel, new THREE.Vector3(0, 0, z * 0.6), new THREE.Vector3(Math.cos(a) * r, Math.sin(a) * r, z), 0.05, rim);
    }
  }
  const merged = mergeStatic(wheel);
  root.add(merged);

  // Gondolas hang from the rim, kept level as the wheel turns.
  const cars: THREE.Group[] = [];
  const n = 12;
  for (let i = 0; i < n; i++) {
    const car = new THREE.Group();
    car.name = "gondola";
    const color = GONDOLA_COLORS[i % GONDOLA_COLORS.length]!;
    kit.rod(car, new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, -0.7, 0), 0.04, "#5c677d");
    kit.box(car, 1.3, 0.8, 1.6, color, 0, -1.6, 0);
    kit.box(car, 1.5, 0.12, 1.8, "#f4f1ea", 0, -0.82, 0);
    root.add(car);
    cars.push(car);
  }
  const place = (t: number) => {
    const turn = t * SPIN;
    merged.rotation.z = turn;
    cars.forEach((car, i) => {
      const a = turn + (i / n) * Math.PI * 2;
      car.position.set(Math.cos(a) * r, hubY + Math.sin(a) * r, 0);
    });
  };
  place(0);
  onTick(root, place);
  return root;
}
